import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const platforms = [
  { name: 'Caribbean Payments Alliance', href: 'https://caribbeanpaymentsalliance.com', external: true },
  { name: 'CloudSync Pro', href: 'https://csyncpro.com', external: true },
  { name: 'BahamasPay', href: 'https://bahamaspay.com', external: true },
  { name: 'APC Estate Flow', href: 'https://apcestateflow.com', external: true },
];

const navLinks = [
  { label: 'Home', href: '#hero' },
  { label: 'About SAG', href: '#about' },
  { label: 'Platforms', href: '#group', dropdown: true },
  { label: 'Industries', href: '#industries' },
  { label: 'Partners', href: '#partners' },
  { label: 'Helpdesk', href: 'https://helpdesk.sebasalliance.com', external: true },
  { label: 'Events', href: 'https://events.sebasalliance.com', external: true },
  { label: 'Contact', href: '#contact' },
];

const CorporateNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-10 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.08)] py-3'
          : 'bg-white/80 backdrop-blur-sm py-4 border-b border-gray-100'
      }`}
      data-testid="corporate-nav"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <a href="#hero" className="flex items-center gap-3 group flex-shrink-0" data-testid="nav-logo">
          <img src="/logos/sag-logo.png" alt="SAG" className="h-9 md:h-10 w-auto transition-transform group-hover:scale-105" />
          <div className="hidden 2xl:block">
            <div className="text-[#0a0a0a] text-[11px] tracking-[0.2em] uppercase font-semibold leading-tight">Sebastian Alliance</div>
            <div className="text-[#C9A227] text-[9px] tracking-[0.25em] uppercase">Corporate Group</div>
          </div>
        </a>

        {/* Desktop nav */}
        <ul className="hidden xl:flex items-center gap-0">
          {navLinks.map((link) => (
            <li key={link.label} className="relative" onMouseEnter={() => link.dropdown && setDropdownOpen(true)} onMouseLeave={() => link.dropdown && setDropdownOpen(false)}>
              <a
                href={link.href}
                target={link.external ? '_blank' : '_self'}
                rel={link.external ? 'noopener noreferrer' : ''}
                className="nav-link-underline flex items-center gap-1 px-3 py-2 text-[#0a0a0a]/70 hover:text-[#C9A227] text-[11px] tracking-[0.08em] uppercase font-medium transition-colors whitespace-nowrap"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
                {link.dropdown && <ChevronDown className="w-3 h-3" />}
              </a>
              {link.dropdown && dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 shadow-2xl py-2 z-50">
                  {platforms.map((p) => (
                    <a
                      key={p.name}
                      href={p.href}
                      target={p.external ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      className="block px-5 py-3 text-[#0a0a0a]/70 hover:text-[#C9A227] hover:bg-[#fafaf7] text-xs tracking-wider transition-colors border-l-2 border-transparent hover:border-[#C9A227]"
                    >
                      {p.name}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <a
          href="#cta-consult"
          className="hidden xl:inline-flex items-center px-4 py-2.5 bg-[#0a0a0a] text-white text-[10px] tracking-[0.12em] uppercase font-bold hover:bg-[#C9A227] hover:text-[#0a0a0a] transition-all hover:-translate-y-0.5 hover:shadow-lg whitespace-nowrap flex-shrink-0"
          data-testid="nav-consult-cta"
        >
          Schedule Consultation
        </a>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="xl:hidden text-[#0a0a0a] p-2"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200 mt-3 max-h-[80vh] overflow-y-auto">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target={link.external ? '_blank' : '_self'}
              rel={link.external ? 'noopener noreferrer' : ''}
              onClick={() => setMobileOpen(false)}
              className="block px-6 py-4 text-[#0a0a0a]/80 hover:text-[#C9A227] hover:bg-[#fafaf7] text-sm tracking-wider uppercase border-b border-gray-100"
            >
              {link.label}
            </a>
          ))}
          {platforms.map((p) => (
            <a
              key={p.name}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-10 py-3 text-[#0a0a0a]/50 hover:text-[#C9A227] text-xs"
            >
              → {p.name}
            </a>
          ))}
          <a
            href="#cta-consult"
            onClick={() => setMobileOpen(false)}
            className="block mx-6 my-4 py-3 bg-[#0a0a0a] text-white text-center text-xs tracking-[0.15em] uppercase font-bold"
          >
            Schedule Consultation
          </a>
        </div>
      )}
    </nav>
  );
};

export default CorporateNav;
